import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, ChevronDown, ChevronRight, LayoutGrid } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReportCatalogCard from "./ReportCatalogCard";
import {
    getGroupedReports,
    getReportsForMode,
    REPORT_GROUP_LABELS,
    REPORT_GROUP_ORDER,
} from "@/pages/reports/reportUtils";
import { getReportModule, getReportModeConfig } from "@/pages/reports/reportModules";
import ReportAnimatedTitle from "@/components/reports/layout/ReportAnimatedTitle";
import {
    reportCard,
    reportContainer,
    reportHeaderBreadcrumb,
    reportHeaderIconWrap,
    reportHubSectionHeader,
    reportMutedText,
    reportPage,
    reportShell,
    reportShellTopBar,
} from "@/components/reports/layout/reportTheme";

function ReportHubSection({ groupKey, reports, moduleId, mode, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const label = REPORT_GROUP_LABELS[groupKey] || groupKey;

    if (!reports?.length) return null;

    return (
        <section className={`${reportCard} overflow-hidden`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={reportHubSectionHeader}
                aria-expanded={open}
            >
                <div className="flex items-center gap-3">
                    {open ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{label}</h2>
                    <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold
                                   bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    >
                        {reports.length}
                    </span>
                </div>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                    >
                        <div className="grid grid-cols-1 gap-3 px-4 sm:px-5 pb-5 pt-4 sm:grid-cols-2 xl:grid-cols-3">
                            {reports.map((report) => (
                                <ReportCatalogCard
                                    key={report.id}
                                    report={report}
                                    moduleId={moduleId}
                                    mode={mode}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

function UnknownModuleFallback({ moduleId }) {
    return (
        <div className={`mx-auto max-w-lg p-8 text-center ${reportCard}`}>
            <p className={reportMutedText}>
                Report module &quot;{moduleId}&quot; is not registered.
            </p>
        </div>
    );
}

const ReportHubPage = ({ moduleId, mode: modeProp = "export" }) => {
    const { t } = useTranslation();
    const mod = getReportModule(moduleId);
    const mode =
        modeProp && mod?.modes?.[modeProp]
            ? modeProp
            : mod?.defaultMode ?? Object.keys(mod?.modes ?? {})[0] ?? modeProp;

    const pageMeta = getReportModeConfig(moduleId, mode);
    const catalog = mod?.catalog ?? [];

    const grouped = useMemo(() => getGroupedReports(catalog, mode), [catalog, mode]);
    const allReports = useMemo(() => getReportsForMode(catalog, mode), [catalog, mode]);
    const liveCount = allReports.filter((r) => r.badge === "LIVE").length;

    if (!mod || !pageMeta) {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={reportPage}>
                <UnknownModuleFallback moduleId={moduleId} />
            </motion.div>
        );
    }

    const statsLine = `${allReports.length} ${t("reports")?.toLowerCase() || "reports"}${
        liveCount > 0 ? ` · ${liveCount} live` : ""
    }`;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={reportPage}>
            <div className={reportContainer}>
                <div className={reportShell}>
                    <div className={reportShellTopBar}>
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={reportHeaderIconWrap}>
                                <BarChart3
                                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                                    strokeWidth={2.2}
                                />
                            </div>
                            <div className="min-w-0">
                                <p className={reportHeaderBreadcrumb}>{pageMeta.breadcrumb}</p>
                                <ReportAnimatedTitle title={pageMeta.title} />
                                <p
                                    className={`mt-0.5 text-[12px] hidden sm:block truncate max-w-lg lg:max-w-2xl ${reportMutedText}`}
                                >
                                    {pageMeta.subtitle} · {statsLine}
                                </p>
                            </div>
                        </div>
                        <div
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg
                                       border border-gray-200 dark:border-gray-700
                                       bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300
                                       text-[12px] font-semibold"
                        >
                            <LayoutGrid className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            {statsLine}
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    {REPORT_GROUP_ORDER.map((groupKey, index) => (
                        <ReportHubSection
                            key={groupKey}
                            groupKey={groupKey}
                            reports={grouped[groupKey]}
                            moduleId={moduleId}
                            mode={mode}
                            defaultOpen={index === 0}
                        />
                    ))}
                </div>
            </div>
        </motion.div>
    );
};

export default ReportHubPage;
